// The device settings the shell itself owns.
import { defineDeviceSetting } from "./device-setting";

export type Theme = "auto" | "light" | "dark";

export const themeSetting = defineDeviceSetting<Theme>({
  key: "trameria-next-theme",
  parse: (raw) => (raw === "light" || raw === "dark" ? raw : "auto"),
});

/** `mod`: ⌘/Ctrl+Enter sends and Enter breaks the line. `enter`: the other way. */
export type SendKey = "mod" | "enter";

export const sendKeySetting = defineDeviceSetting<SendKey>({
  key: "trameria-send-key",
  parse: (raw) => (raw === "enter" ? "enter" : "mod"),
});

/**
 * The least height of an opened item's free answer in a PC's post box,
 * dragged on the line above it. 0: as tall as what is written.
 */
export const turnFieldHeightSetting = defineDeviceSetting<number>({
  key: "trameria-turn-field-height",
  parse: (raw) => {
    const n = raw === null ? Number.NaN : Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
  },
});

const width = (key: string, fallback: number, min: number, max: number) =>
  defineDeviceSetting<number>({
    key,
    parse: (raw) => {
      const n = raw === null ? Number.NaN : Number(raw);
      return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
    },
  });

export const SIDEBAR_MIN = 180;
export const SIDEBAR_MAX = 320;
export const SIDEBAR_DEFAULT = 216;
export const LIST_MIN = 220;
export const LIST_MAX = 400;
export const LIST_DEFAULT = 292;

export const sidebarWidthSetting = width(
  "trameria-next-sidebar-width",
  SIDEBAR_DEFAULT,
  SIDEBAR_MIN,
  SIDEBAR_MAX,
);
/** The sidebar folded to its icons: as wide as the rail. */
export const SIDEBAR_COLLAPSED = 52;
/** Dragging the sidebar narrower than this folds it (and wider unfolds it). */
export const SIDEBAR_SNAP_BELOW = 120;

/** Whether the sidebar is folded to its icons. Kept apart from its width, so unfolding returns to that width. */
export const sidebarCollapsedSetting = defineDeviceSetting<boolean>({
  key: "trameria-next-sidebar-collapsed",
  parse: (raw) => raw === "1",
  serialize: (v) => (v ? "1" : "0"),
});

export const listWidthSetting = width(
  "trameria-next-list-width",
  LIST_DEFAULT,
  LIST_MIN,
  LIST_MAX,
);
